import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getMenu,
  createItem,
  updateItem,
  getModifierGroups,
  getInventoryItems,
} from '../services/api';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import ItemForm from '../components/ItemForm';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { trackEvent } from '../utils/analytics';
import { storage } from '../utils/storage';
import './ItemFormPage.css';

function ItemFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemId } = useParams();
  const { t } = useTranslation();
  const isEditMode = !!itemId;
  const { setDynamicLabel } = useBreadcrumb();

  const [menu, setMenu] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemData, setItemData] = useState(location.state?.item || null);
  const [categoryId] = useState(location.state?.categoryId || '');
  const [isDirty, setIsDirty] = useState(false);
  const { showModal, confirmNavigation, cancelNavigation } = useUnsavedChanges(isDirty);

  const handleDirtyChange = useCallback((dirty) => {
    setIsDirty(dirty);
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditMode && itemData?.name) {
      setDynamicLabel(itemData.name);
    }
  }, [isEditMode, itemData, setDynamicLabel]);

  const loadData = async () => {
    try {
      const response = await getMenu();
      const activeMenu = (response.menu || [])
        .filter((cat) => cat.status !== 'deleted')
        .map((cat) => ({
          ...cat,
          items: (cat.items || []).filter((item) => item.status !== 'deleted'),
        }));
      setMenu(activeMenu);

      const restaurantId = storage.get('selectedRestaurantId');
      if (restaurantId) {
        const modData = await getModifierGroups(restaurantId);
        setModifierGroups(modData.modifierGroups || []);

        const invData = await getInventoryItems();
        setInventoryItems(invData.items || []);
      }

      // If edit mode and no state passed, find item from loaded menu
      if (isEditMode && !location.state?.item) {
        for (const cat of activeMenu) {
          const found = (cat.items || []).find((i) => i.itemId === itemId);
          if (found) {
            setItemData(prepareItemForEdit(found, cat.categoryId));
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const prepareItemForEdit = (item, catId) => ({
    ...item,
    categoryId: catId,
    price: item.price.toString(),
    tags: (item.tags || []).join(', '),
    allergens: (item.allergens || []).join(', '),
    modifierGroups: item.modifierGroups || [],
    isFeatured: item.isFeatured || false,
    isDailySpecial: item.isDailySpecial || false,
    isPromotion: item.isPromotion || false,
    originalPrice: item.originalPrice || '',
    promotionType: item.promotionType || '',
    promotionBadge: item.promotionBadge || '',
    highlightColor: item.highlightColor || '',
    promotionValidFrom: item.promotionValidFrom || '',
    promotionValidTo: item.promotionValidTo || '',
    promotionSchedule: item.promotionSchedule || null,
    storyOrder: item.storyOrder || 0,
  });

  const transformFormData = (formData) => ({
    ...formData,
    price: parseFloat(formData.price),
    tags:
      typeof formData.tags === 'string'
        ? formData.tags
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t)
        : formData.tags,
    allergens:
      typeof formData.allergens === 'string'
        ? formData.allergens
            .split(',')
            .map((a) => a.trim())
            .filter((a) => a)
        : formData.allergens,
  });

  const handleCreate = async (formData) => {
    try {
      await createItem(transformFormData(formData));
      trackEvent('menu_item_created', {
        restaurant_id: storage.get('selectedRestaurantId'),
        organization_id: storage.get('selectedOrganizationId'),
        has_image: !!formData.imageUrl,
        has_modifiers: (formData.modifierGroupIds?.length || 0) > 0,
        category_id: formData.categoryId || categoryId,
      });
      setIsDirty(false);
      navigate('/menu');
    } catch (error) {
      alert(t('menu.createItemError'));
    }
  };

  const handleUpdate = async (formData) => {
    try {
      await updateItem(itemId, transformFormData(formData));
      setIsDirty(false);
      navigate('/menu');
    } catch (error) {
      alert(t('menu.updateItemError'));
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="item-form-page">
      <div className="form-header">
        <h1>{isEditMode ? t('menu.editItem') : t('menu.newItem')}</h1>
        <p>
          {isEditMode
            ? 'Modifica la información del producto'
            : 'Completa la información para crear un nuevo producto'}
        </p>
      </div>

      <ItemForm
        initialData={isEditMode ? itemData : categoryId ? { categoryId } : undefined}
        categories={menu}
        modifierGroups={modifierGroups}
        inventoryItems={inventoryItems}
        onSubmit={isEditMode ? handleUpdate : handleCreate}
        onCancel={() => navigate('/menu')}
        onDirtyChange={handleDirtyChange}
        submitLabel={isEditMode ? t('common.save') : t('menu.createItem')}
        draftKey={isEditMode ? itemId : 'new'}
      />

      {showModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsaved-changes-title"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h2 id="unsaved-changes-title">{t('common.unsavedChangesTitle')}</h2>
            </div>
            <div className="modal-body">
              <p>{t('common.unsavedChangesMessage')}</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={cancelNavigation}>
                {t('common.unsavedChangesStay')}
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmNavigation}>
                {t('common.unsavedChangesLeave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ItemFormPage;
